import { useRef, useState, useMemo, useEffect, useContext } from 'react';
import { Color, Vector2, Vector3, Raycaster, MathUtils, IcosahedronGeometry } from 'three';
import { MeshTransmissionMaterial, Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import TextGroup from './TextGroup';
import LanguageContext from '../../Contexts/Language';
import createGradientTexture from './createGradientTexture';
import { playCrystalClicked } from '../../Sounds/Sounds';
import { DoubleSide } from 'three';

export default function Model(props) {
    const {language} = useContext(LanguageContext);
    const { width: w} = useThree((state) => state.viewport);
    const [backgroundColor, setBackgroundColor] = useState(document.body.className === "light-theme" ? new Color('#ffffff') : new Color('#0A0A0A'));
    const [zoom, setZoom] = useState(window.outerWidth / window.innerWidth);
    const [color, setColor] = useState(document.body.className === "light-theme" ? new Color('#0A0A0A') : new Color('#ffffff'))
    const crystal = useRef(null);
    const wholeCrystal = useRef(null);
    const nameText = useRef(null);
    const hiText = useRef(null);
    const imText = useRef(null);
    const devText = useRef(null);
    const wholeDevText = useRef(null);
    const speed = 0.07
    const rotationSpeed = 0.007
    const rotationZ = 31.72 * Math.PI / 180;
    const speedMultiplier = speed * 2.5;
    const { camera, gl } = useThree();
    const raycaster = new Raycaster();
    const geometry = useMemo(() => new IcosahedronGeometry(0.75, 0), []);
    const materialProps = useMemo(() => ({
        thickness: 0.25,
        roughness: 0,
        transmission: 1,
        ior: 1.2,  
        chromaticAberration: 0.15, 
        backside: true, 
        anisotropy: 0,
        distortion: 0.5,
        toneMapped: false,
        color: "#F0F0F0",
    }), []);

    useEffect(() => {
        const handleClick = (event) => {
        if (!crystal.current) return;

        const bounds = gl.domElement.getBoundingClientRect();
        const mouse = new Vector2(
            ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
            -((event.clientY - bounds.top) / bounds.height) * 2 + 1
        );

        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObject(crystal.current, true);

            if (intersects.length > 0) {
                console.log('Kliknięto CRYSTAL!');
                playCrystalClicked();
            }
        };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
    }, [playCrystalClicked]);

    useEffect(() => {
        function reSize() {
            setZoom(window.outerWidth / window.innerWidth)
        }
        window.addEventListener("resize", reSize);
        return () => {
        window.removeEventListener("resize", reSize);
        };
    }, []);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const isLight = document.body.className === "light-theme";
            setBackgroundColor(new Color(isLight ? '#ffffff' : '#0A0A0A'));
            setColor(new Color(isLight ? '#0A0A0A' : '#ffffff'));
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        const isLight = document.body.className === "light-theme";
        setBackgroundColor(new Color(isLight ? '#ffffff' : '#0A0A0A'));
        setColor(new Color(isLight ? '#0A0A0A' : '#ffffff'));

        return () => observer.disconnect();
        }, []); 

    useFrame( () => {
        //i hate this
        const movedFactor = props.moved / (100 / (zoom)) - 2;
        crystal.current.rotation.y += rotationSpeed;
            if (props.moved !== 0) {
                //When moved
                if(crystal.current.position.y !== 3.495){
                    nameText.current.fontSize = MathUtils.lerp(nameText.current.fontSize, 0.45, speed);
                    nameText.current.position.lerp(new Vector3(-w + 1.08, 6.985, -5), speed);

                    hiText.current.position.lerp(new Vector3(language.crystal.positions.hiText.moved.x, language.crystal.positions.hiText.moved.y, language.crystal.positions.hiText.moved.z), speed);
                    hiText.current.material.opacity = MathUtils.lerp(hiText.current.material.opacity, 0, speedMultiplier);
            
                    crystal.current.scale.lerp(new Vector3(0.1875, 0.1875, 0.1875), speed);
                    crystal.current.position.lerp(new Vector3(0, 3.495, 0), speed);
                    crystal.current.rotation.x = 31.72 * Math.PI / 180;

                    imText.current.position.lerp(new Vector3(language.crystal.positions.imText.moved.x,language.crystal.positions.imText.moved.y, language.crystal.positions.imText.moved.z), speed);
                
                    wholeCrystal.current.position.lerp(new Vector3(0,0,0), speed);
                }

                if(movedFactor >= -2 && movedFactor <= 1.5){
                    devText.current.position.lerp(new Vector3(language.crystal.positions.devText.moved.x,language.crystal.positions.devText.moved.y, language.crystal.positions.devText.moved.z), speed);
                    const opacity = MathUtils.lerp(devText.current.material.opacity, 1, 0.12);
                    if (devText.current.material.opacity !== opacity) {
                        devText.current.material.opacity = opacity;
                        devText.current.material.map = createGradientTexture(opacity * 100);
                    }
                    wholeDevText.current.position.lerp(new Vector3((w * 2.2) - (14 * 2.2) < 0 ? -(w * 0.5) + 7 : -w + 16,0,(w * 2.2) - (14 * 2.2) < 0 ? (w * 2.2) - (14 * 2.2) : 0), speed);
                }
                else{
                    devText.current.position.lerp(new Vector3(language.crystal.positions.devText.moved.x,language.crystal.positions.devText.moved.y, language.crystal.positions.devText.moved.z),speed);
                    const opacity = MathUtils.lerp(devText.current.material.opacity, 0, (movedFactor > 3 ? 1 : 0.05));
                    if (devText.current.material.opacity !== opacity) {
                        devText.current.material.opacity = opacity;
                        devText.current.material.map = createGradientTexture(opacity * 100);
                    }
                    wholeDevText.current.position.lerp(new Vector3(0,0,w < 10 ? w - 15 : 0), speed);
                }
                
                if(movedFactor < 8 && movedFactor >= 1.5){
                    imText.current.position.lerp(new Vector3(language.crystal.positions.imText.inRange.x, language.crystal.positions.imText.inRange.y, language.crystal.positions.imText.inRange.z), speed);
                }
                else if (movedFactor > 7) {
                    imText.current.position.lerp(new Vector3(language.crystal.positions.imText.outOfRange.x, (movedFactor - 8) * (w < 10 ? 1.44 : 1.13), language.crystal.positions.imText.outOfRange.z), 1);
                } 
            } 
            else {
                //When default
                if(crystal.current.position.y !== 0){
                    nameText.current.fontSize = MathUtils.lerp(nameText.current.fontSize, 1.45, speed);
                    nameText.current.position.lerp(new Vector3(language.crystal.positions.nameText.default.x, language.crystal.positions.nameText.default.y, language.crystal.positions.nameText.default.z), speed);
                
                    hiText.current.position.lerp(new Vector3(language.crystal.positions.hiText.default.x, language.crystal.positions.hiText.default.y, language.crystal.positions.hiText.default.z), speed);
                    hiText.current.material.opacity = MathUtils.lerp(hiText.current.material.opacity, 1, speed);
                
                    imText.current.position.lerp(new Vector3(language.crystal.positions.imText.default.x, language.crystal.positions.imText.default.y, language.crystal.positions.imText.default.z), speed);
                
                    crystal.current.scale.lerp(new Vector3(1, 1, 1), speed);
                    crystal.current.position.lerp(new Vector3(language.crystal.positions.crystalObj.default.x, language.crystal.positions.crystalObj.default.y, language.crystal.positions.crystalObj.default.z), speed);
                    crystal.current.rotation.x = 0;
                    devText.current.position.lerp(new Vector3(language.crystal.positions.devText.default.x, language.crystal.positions.devText.default.y, language.crystal.positions.devText.default.z), speed);
                    devText.current.material.opacity = MathUtils.lerp(devText.current.material.opacity, 0, speedMultiplier);
                
                    wholeDevText.current.position.lerp(new Vector3(0,0,0), speed);
                    wholeCrystal.current.position.lerp(new Vector3(0,0,w < 10 ? w - 13 : 0), speed);
                }
            }
    })

    return (
        <group ref={wholeCrystal}>
            <group ref={wholeDevText} position={[0,0,0]}>
                    <Text ref={hiText} font={'/fonts/PPNeueMontreal-Bold.otf'} fontSize={1.45} position={[-75, 0, -5]} color={color}>
                        {language.crystal.hi}
                    </Text>
                    <Text ref={devText} font={'/fonts/PPNeueMontreal-Bold.otf'} fontSize={1.45} position={[24, 0, -5]} color={color}>
                        {language.crystal.dev}
                    </Text>
                    <Text ref={imText} font={'/fonts/PPNeueMontreal-Bold.otf'} fontSize={1.45} position={[-50, 0, -5]} color={color}>
                        {language.crystal.im}
                    </Text>
            </group>
            <Text ref={nameText} font={'/fonts/PPNeueMontreal-Bold.otf'} position={[-12.5, 0, -5]} fontSize={1.45} color={color}>
                Nikita
            </Text>
            <TextGroup scrollValue={props.moved} position={[language.crystal.positions.textGroup.default.x, language.crystal.positions.textGroup.default.y, language.crystal.positions.textGroup.default.z]} zoom={zoom}/>
            <mesh ref={crystal} position={[1.125,-0.0001,0]} rotation={[0,0,rotationZ]} scale={[0,0,0]}>
                <primitive object={geometry} />
                {localStorage.getItem("quality") === "low" ? (
                  <meshBasicMaterial color={color} opacity={0.25} transparent side={DoubleSide} wireframe />
                ) : (
                  <MeshTransmissionMaterial {...materialProps} background={backgroundColor} />
                )}
            </mesh>
        </group>
    )
}
